import { selectCurrency } from '@/redux/config/configSlice';
import { InboxOutlined, TagsOutlined } from '@ant-design/icons';
import { sumBy } from 'lodash';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModifierType } from '../../redux/data/dataTypes';
import { useAppSelector } from '../../redux/hooks';

interface ModifierListProps {
  searchTxt: string;
  onItemPress: (modifier: ModifierType) => void;
  className?: string;
}

interface ModifierItemProps {
  modifier: ModifierType;
  onItemPress: (modifier: ModifierType) => void;
}

const ModifierItem: FC<ModifierItemProps> = ({ modifier, onItemPress }) => {
  const { t, i18n } = useTranslation();
  const currency = useAppSelector(selectCurrency);
  const totalPrice = sumBy(modifier.options, (option) => option?.price ?? 0);
  const lang = i18n.language;
  const isArabic = lang === 'ar';

  return (
    <div
      className="group cursor-pointer transition-all duration-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 rounded-lg p-2.5 shadow-sm hover:shadow-md"
      onClick={() => onItemPress(modifier)}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#e6f7ff] dark:bg-gray-500 text-frenchBlue dark:text-white transition-all duration-300 group-hover:scale-105 flex-shrink-0">
          <TagsOutlined className="text-base" />
        </div>
        <div className="flex flex-col flex-1 min-h-[45px]">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-frenchBlue dark:group-hover:text-white transition-colors duration-300">
              {isArabic && modifier.nameAr ? modifier.nameAr : modifier.name}
            </span>
            <div className="min-w-[45px] text-right">
              <div className="bg-gray-100 dark:bg-gray-500 rounded-lg px-2 py-0.5">
                <span className="text-xs font-bold text-frenchBlue dark:text-white whitespace-nowrap">
                  {totalPrice} {currency}
                </span>
              </div>
            </div>
          </div>
          <div className="text-[10px] text-gray-600 dark:text-gray-300 mt-1">
            {modifier.options.length} {t('common:item')}
            {modifier.options.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

const ModifierList: FC<ModifierListProps> = ({ searchTxt, onItemPress, className }) => {
  const { t } = useTranslation();
  const { modifiers } = useAppSelector((state) => state.data);

  const filteredModifiers = modifiers.filter((modifier) =>
    modifier.name.toLowerCase().includes(searchTxt.toLowerCase())
  );

  if (filteredModifiers.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-6 bg-[#f6f8fc] dark:bg-gray-700 rounded-xl">
        <div className="text-center p-5 rounded-xl bg-white dark:bg-gray-600 shadow-sm">
          <div className="flex justify-center mb-3">
            <div className="w-14 h-14 flex items-center justify-center bg-[#e6f7ff] dark:bg-gray-500 text-frenchBlue dark:text-white rounded-full">
              <InboxOutlined className="text-xl" />
            </div>
          </div>
          <div className="text-base font-semibold text-gray-800 dark:text-white mb-1">
            {t('common:no_modifiers_found')}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-300">Try adjusting your search</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f6f8fc] dark:bg-gray-700 rounded-xl p-1 scrollable">
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 ${className}`}>
        {filteredModifiers.map((modifier) => (
          <ModifierItem key={modifier.name} modifier={modifier} onItemPress={onItemPress} />
        ))}
      </div>
    </div>
  );
};

export default ModifierList;
